import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faLightbulb,
  faInfoCircle,
  faEnvelope,
  faTimes,
  faLink,
  faCheckCircle,
  faShieldAlt,
} from '@fortawesome/free-solid-svg-icons';
import { useMenu } from '../context/MenuContext';

// Types
interface MenuLink {
  to: string;
  label: string;
  icon: any;
}

// Constants for better maintainability
const SCROLL_DELAY = 50;
const SCROLL_BEHAVIOR = 'smooth' as ScrollBehavior;

// Navigation links data
const MENU_LINKS: readonly MenuLink[] = [
  { to: '/', label: 'Home', icon: faHome },
  { to: '/ai-solutions', label: 'Solutions', icon: faLightbulb },
  { to: '/departments', label: 'Departments', icon: faShieldAlt },
  { to: '/integrations', label: 'Integrations', icon: faLink },
  { to: '/about', label: 'About Us', icon: faInfoCircle },
  { to: '/contact', label: 'Contact', icon: faEnvelope },
] as const;

const SideMenu: React.FC = () => {
  const { isMenuOpen, closeMenu } = useMenu();
  const location = useLocation();
  const navigate = useNavigate();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Memoize navigation links to prevent recreation
  const menuLinks = useMemo(() => MENU_LINKS, []);

  // Memoized navigation handler with error handling
  const handleNav = useCallback((to: string) => {
    try {
      // Close menu first
      closeMenu();

      // Navigate to the new page
      navigate(to);

      // Force scroll to top after navigation
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: SCROLL_BEHAVIOR
        });
      }, SCROLL_DELAY);
    } catch (error) {
      console.error('SideMenu: Error during navigation:', error);
    }
  }, [closeMenu, navigate]);

  // Memoized close handler
  const handleClose = useCallback(() => {
    try {
      closeMenu();
    } catch (error) {
      console.error('SideMenu: Error closing menu:', error);
    }
  }, [closeMenu]);

  useEffect(() => {
    if (!isMenuOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeMenu, isMenuOpen]);

  if (!isMenuOpen) return null;

  return (
    <nav id="mobile-side-menu" className="side-menu open" aria-label="Main menu">
      <button
        ref={closeButtonRef}
        className="side-menu-close"
        onClick={handleClose}
        aria-label="Close menu"
        type="button"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
      <ul>
        {menuLinks.map(link => (
          <li key={link.to}>
            <button
              type="button"
              className={location.pathname === link.to ? 'active' : ''}
              onClick={() => handleNav(link.to)}
              aria-label={`Navigate to ${link.label}`}
              aria-current={location.pathname === link.to ? 'page' : undefined}
            >
              <FontAwesomeIcon icon={link.icon} className="side-menu-icon" />
              <span>{link.label}</span>
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={() => handleNav('/audit')}
            className={location.pathname === '/audit' ? 'active' : ''}
            aria-label="Navigate to Free Med Spa Audit"
            aria-current={location.pathname === '/audit' ? 'page' : undefined}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="side-menu-icon" />
            <span style={{ display: 'flex', flexDirection: 'column' }}>
              <span>Free Med Spa</span>
              <span>Automation Audit</span>
            </span>
          </button>
        </li>
      </ul>
    </nav>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(SideMenu);
